import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User } from '../types';
import { authApi, AUTH_UNAUTHORIZED_EVENT } from '../services/api';
import { signalRService } from '../services/signalr';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (nome: string, password: string, rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.me();
      if (response.success && response.data) {
        setUser({
          id: response.data.id,
          nome: response.data.nome,
          role: response.data.role,
          roleId: response.data.roleId,
          ativo: response.data.ativo,
          ultimoLogin: response.data.ultimoLogin,
        });
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Sessão expirada/revogada: API devolve 401 -> limpa user e SignalR
  useEffect(() => {
    const onUnauthorized = () => setUser(null);
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = async (nome: string, password: string, rememberMe: boolean) => {
    const response = await authApi.login({ nome, password, rememberMe });
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Erro no login');
    }
    setUser({
      id: response.data.utilizadorId,
      nome: response.data.nome,
      role: response.data.role,
      roleId: response.data.roleId,
      ativo: true,
    });
  };

  const logout = async () => {
    await authApi.logout();
    await signalRService.stopAll();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        checkAuth,
        isAuthenticated: !!user,
        isAdmin: (() => {
          const raw = String(user?.role ?? '').trim().toLowerCase();
          if (user?.roleId !== undefined) return user.roleId <= 1;
          return raw === 'admin' || raw === 'superadmin' || raw === '0' || raw === '1';
        })(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}